// The live kitchen display (spec/13 §6.2/§6.3, F-FNB-04/-05/-06), laid out like a KDS: four columns —
// New, Accepted, Preparing, Ready — of tickets, oldest first. A ticket's header takes its age: calm under five
// minutes, the warn tint and "Waiting" from five, the danger tint and "Late" from ten, always in words.
// Each ticket shows its order number ("#0421", F-FNB-04), whether the order is paid, its preparation
// station, where it is **delivered** (a station or the counter, F-FNB-06) and its items with modifiers and
// notes; one big bump button moves it on (Accept → Preparing → Ready → Delivered), with Recall and Print
// beside it (F-FNB-05). The last 10 delivered tickets wait in a Recall strip below.
//
// The board is driven by the `kitchen.ticket.*` push on `venue:{v}:kitchen` (spec/09 §6.3), consumed by
// the console's WebSocket client (`realtime.ts`, shared with the Floor); when the socket is not live it
// refetches the snapshot on an interval and on focus (`queries.ts`). Reading needs `kitchen:read`;
// advancing and recalling need `kitchen:update`, and the server checks both again on every request. The
// F&B menu setup stays one link away. With `orders:void` a ticket also offers Cancel order, which opens
// the order in a dialog and cancels it — and its other tickets — after a confirm (F-POS-15): a counter or
// kiosk order is a sale (cancelled while unpaid), an older one a food order.

import type { KitchenTicket, KitchenTicketState, Station } from '@clavian/api-client';
import { formatOrderNumber } from '@clavian/domain/menu-choice';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, cx, EmptyState, Skeleton } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { useStations } from '../../stations/queries';
import { Forbidden, Notice, Page, Section } from '../../ui';
import { PrintOutcomeNote, usePrintJobHandler, type PrintOutcome } from '../printing/outcome';
import { SETTINGS_HARDWARE, usePrinterName } from '../printing/queries';
import { CancelOrderDialog, type CancelTarget } from './CancelOrderDialog';
import { useAdvanceKitchenTicket, useKitchenTickets, usePrintKitchenTicket } from './queries';
import { useKitchenLive } from './realtime';

import './kitchen.css';

const KITCHEN_READ = 'kitchen:read';
const KITCHEN_UPDATE = 'kitchen:update';
const ORDERS_VOID = 'orders:void';

/** The columns of the active board, in the order a ticket moves through them (spec/13 §6.2). */
const BOARD_STATES: readonly KitchenTicketState[] = ['new', 'accepted', 'preparing', 'ready'];

/** The next state a ticket advances to, and the previous it recalls to (spec/13 §6.2, F-FNB-04). */
const FORWARD: Partial<Record<KitchenTicketState, KitchenTicketState>> = { new: 'accepted', accepted: 'preparing', preparing: 'ready', ready: 'delivered' };
const BACKWARD: Partial<Record<KitchenTicketState, KitchenTicketState>> = { accepted: 'new', preparing: 'accepted', ready: 'preparing', delivered: 'ready' };

export function KitchenPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const canRead = access.status === 'ready' && access.can(KITCHEN_READ);
  const canUpdate = access.status === 'ready' && access.can(KITCHEN_UPDATE);
  const canVoid = access.status === 'ready' && access.can(ORDERS_VOID);
  const canReadPrinters = access.status === 'ready' && access.can(SETTINGS_HARDWARE);
  const [cancelling, setCancelling] = useState<CancelTarget | null>(null);
  // The last print from this board and where it went, shown on its ticket (F-FNB-05). Each click is a new job.
  const [printed, setPrinted] = useState<{ readonly ticketId: string; readonly outcome: PrintOutcome } | null>(null);

  const active = useKitchenTickets(venue.organizationId, venue.id, undefined, canRead);
  const delivered = useKitchenTickets(venue.organizationId, venue.id, 'delivered', canRead);
  const stations = useStations(venue.organizationId, venue.id);
  // Apply the live `kitchen.ticket.*` push to both boards while the socket is live; else the boards poll.
  useKitchenLive(venue.organizationId, venue.id);

  const advance = useAdvanceKitchenTicket(venue.organizationId, venue.id);
  const print = usePrintKitchenTicket(venue.organizationId, venue.id);
  const handleJob = usePrintJobHandler();
  const printerName = usePrinterName(venue.organizationId, venue.id, canReadPrinters);
  const now = useNow();

  // Station id → its label, so a ticket can name where it is delivered (F-FNB-06).
  const stationLabel = useMemo(() => {
    const rows = (stations.data?.pages ?? []).flatMap((page) => page.data);
    const byId = new Map<string, string>();
    for (const station of rows) byId.set(station.id, labelForStation(station, t));
    return (stationId: string | null): string => (stationId === null ? t('kitchen.counter') : (byId.get(stationId) ?? t('kitchen.station')));
  }, [stations.data, t]);
  // Where a ticket goes, as a sentence: "To PC-07", "To the counter" — or "To a PC" when the station is not on this
  // venue's list any more.
  const destination = useMemo(() => {
    const rows = (stations.data?.pages ?? []).flatMap((page) => page.data);
    const byId = new Map<string, string>();
    for (const station of rows) byId.set(station.id, labelForStation(station, t));
    return (stationId: string | null): string => {
      if (stationId === null) return t('kitchen.deliverToCounter');
      const name = byId.get(stationId);
      return name === undefined ? t('kitchen.deliverToPc') : t('kitchen.deliverTo', { destination: name });
    };
  }, [stations.data, t]);

  function onPrint(ticket: KitchenTicket): void {
    setPrinted(null);
    print.mutate(
      { ticketId: ticket.id },
      {
        onSuccess: (result) => {
          const printer = printerName(result.job.printerId) ?? t('printing.kitchenPrinter');
          setPrinted({ ticketId: ticket.id, outcome: handleJob(result.job, result.target, orderTag(result.ticket), printer) });
        },
        onError: () => setPrinted({ ticketId: ticket.id, outcome: { kind: 'failed' } }),
      },
    );
  }

  const menuLink = (
    <Button variant="secondary" asChild>
      <Link to="/v/$venueSlug/menu" params={{ venueSlug: venue.slug }}>
        {t('kitchen.menuSetup')}
      </Link>
    </Button>
  );

  if (access.status === 'ready' && !canRead) {
    return (
      <Page title={t('kitchen.title')}>
        <Forbidden message={t('kitchen.notPermitted')} />
      </Page>
    );
  }

  if (active.data === undefined) {
    return (
      <Page width="wide" title={t('kitchen.title')} description={t('kitchen.intro')} actions={menuLink}>
        {active.isError ? (
          <LoadFailure error={active.error} message={t('kitchen.unavailable')} onRetry={() => void active.refetch()} />
        ) : (
          <div className="kitchen__board" aria-busy="true" role="status" aria-label={t('app.loading')}>
            {BOARD_STATES.map((state) => (
              <Skeleton key={state} height="18rem" />
            ))}
          </div>
        )}
      </Page>
    );
  }

  const byState = (state: KitchenTicketState): KitchenTicket[] => active.data.filter((ticket) => ticket.state === state).sort((a, b) => a.placedAt.localeCompare(b.placedAt));
  const recall = [...(delivered.data ?? [])].sort((a, b) => (b.deliveredAt ?? '').localeCompare(a.deliveredAt ?? '')).slice(0, 10);

  return (
    <Page width="wide" title={t('kitchen.title')} description={t('kitchen.intro')} actions={menuLink}>
      {access.status === 'ready' && !canUpdate ? <Notice role="note">{t('kitchen.readOnly')}</Notice> : null}

      <div className="kitchen__board" role="list" aria-label={t('kitchen.boardLabel')}>
        {BOARD_STATES.map((state) => (
          <TicketColumn
            key={state}
            state={state}
            title={t(`kitchen.state.${state}`)}
            tickets={byState(state)}
            now={now}
            canUpdate={canUpdate}
            busy={advance.isPending || print.isPending}
            destination={destination}
            onAdvance={(ticket, to) => advance.mutate({ ticketId: ticket.id, state: to })}
            onPrint={onPrint}
            printed={printed}
            {...(canVoid ? { onCancel: (ticket: KitchenTicket) => setCancelling(cancelTargetOf(ticket)) } : {})}
          />
        ))}
      </div>

      <CancelOrderDialog
        organizationId={venue.organizationId}
        venueId={venue.id}
        target={cancelling}
        onClose={() => setCancelling(null)}
      />

      {recall.length > 0 ? (
        <Section title={t('kitchen.recallTitle')} description={t('kitchen.recallHint')} body="rows">
          <ul className="kitchen__recall-list" aria-label={t('kitchen.recallLabel')}>
            {recall.map((ticket) => (
              <li key={ticket.id} className="kitchen__recall-item">
                <span className="kitchen__order console-figure">{orderTag(ticket)}</span>
                <span className="console-muted">{stationLabel(ticket.stationId)}</span>
                <Button size="sm" variant="ghost" icon={<Icon name="history" />} disabled={!canUpdate} busy={advance.isPending} onClick={() => advance.mutate({ ticketId: ticket.id, state: 'ready' })}>
                  {t('kitchen.recall')}
                </Button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </Page>
  );
}

function TicketColumn({
  state,
  title,
  tickets,
  now,
  canUpdate,
  busy,
  destination,
  onAdvance,
  onPrint,
  printed,
  onCancel,
}: {
  readonly state: KitchenTicketState;
  readonly title: string;
  readonly tickets: readonly KitchenTicket[];
  readonly now: number;
  readonly canUpdate: boolean;
  readonly busy: boolean;
  readonly destination: (stationId: string | null) => string;
  readonly onAdvance: (ticket: KitchenTicket, to: KitchenTicketState) => void;
  readonly onPrint: (ticket: KitchenTicket) => void;
  readonly printed: { readonly ticketId: string; readonly outcome: PrintOutcome } | null;
  readonly onCancel?: (ticket: KitchenTicket) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <section className={`kitchen-col kitchen-col--${state}`} role="listitem" aria-label={title}>
      {/* The column names its stage in the display face with its count as a key cap, over a rail in the stage's
          colour (ADR-0043): ember for what has just come in, green for what is ready to go out. */}
      <header className="kitchen-col__head">
        <span className="kitchen-col__title">{title}</span>
        <span className="kitchen-col__count console-keycap">{tickets.length}</span>
      </header>
      {tickets.length === 0 ? (
        <EmptyState size="compact" icon={<Icon name="check" />} title={t('kitchen.columnEmpty')} />
      ) : (
        <ul className="kitchen-col__list">
          {tickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} now={now} canUpdate={canUpdate} busy={busy} destination={destination(ticket.stationId)} onAdvance={onAdvance} onPrint={onPrint} printed={printed?.ticketId === ticket.id ? printed.outcome : null} {...(onCancel === undefined ? {} : { onCancel })} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TicketCard({
  ticket,
  now,
  canUpdate,
  busy,
  destination,
  onAdvance,
  onPrint,
  printed,
  onCancel,
}: {
  readonly ticket: KitchenTicket;
  readonly now: number;
  readonly canUpdate: boolean;
  readonly busy: boolean;
  readonly destination: string;
  readonly onAdvance: (ticket: KitchenTicket, to: KitchenTicketState) => void;
  readonly onPrint: (ticket: KitchenTicket) => void;
  readonly printed: PrintOutcome | null;
  readonly onCancel?: (ticket: KitchenTicket) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const forward = FORWARD[ticket.state];
  const backward = BACKWARD[ticket.state];
  const waited = minutesSince(ticket.placedAt, now);
  const tone = ageTone(waited);

  return (
    <li className={cx('kitchen-card', `kitchen-card--${tone}`)}>
      <header className="kitchen-card__head">
        <span className="kitchen__order console-figure">{orderTag(ticket)}</span>
        <span className="kitchen-card__age">
          {tone === 'fresh' ? null : <Icon name={tone === 'late' ? 'alert' : 'clock'} />}
          <span aria-hidden="true">{t(`kitchen.age.${tone}`, { minutes: waited })}</span>
          <span className="clav-visually-hidden">{t('kitchen.waited', { minutes: waited })}</span>
        </span>
      </header>

      <div className="kitchen-card__meta">
        <span className="kitchen-card__dest">
          <Icon name={ticket.stationId === null ? 'cup' : 'pin'} />
          {destination}
        </span>
        <Badge tone={ticket.area === 'bar' ? 'info' : 'neutral'}>{t(`kitchen.area.${ticket.area}`)}</Badge>
        <Badge tone={ticket.paid ? 'ok' : 'warn'} dot>
          {ticket.paid ? t('kitchen.paid') : t('kitchen.unpaid')}
        </Badge>
      </div>

      <ul className="kitchen-card__items">
        {ticket.items.map((item) => (
          <li key={item.id} className="kitchen-card__item">
            <span className="kitchen-card__qty console-num">{item.quantity}×</span>
            <span className="kitchen-card__item-body">
              <span className="kitchen-card__item-name">{item.name}</span>
              {item.modifiers.length > 0 ? <span className="kitchen-card__mods">{item.modifiers.join(', ')}</span> : null}
              {item.note !== null ? <span className="kitchen-card__note">{item.note}</span> : null}
            </span>
          </li>
        ))}
      </ul>

      {ticket.note !== null ? <p className="kitchen-card__ticket-note">{ticket.note}</p> : null}

      <div className="kitchen-card__actions">
        {forward !== undefined ? (
          <Button variant="primary" block disabled={!canUpdate} busy={busy} onClick={() => onAdvance(ticket, forward)}>
            {t(`kitchen.advance.${ticket.state}`)}
          </Button>
        ) : null}
        <div className="kitchen-card__secondary">
          {backward !== undefined ? (
            <Button size="sm" variant="ghost" icon={<Icon name="history" />} disabled={!canUpdate} busy={busy} onClick={() => onAdvance(ticket, backward)}>
              {t('kitchen.recall')}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" icon={<Icon name="print" />} busy={busy} onClick={() => onPrint(ticket)}>
            {t('kitchen.print')}
          </Button>
          {onCancel === undefined ? null : (
            <Button size="sm" variant="ghost" icon={<Icon name="close" />} aria-label={t('kitchen.cancel.openFor', { order: orderTag(ticket) })} onClick={() => onCancel(ticket)}>
              {t('kitchen.cancel.open')}
            </Button>
          )}
        </div>
        <PrintOutcomeNote outcome={printed} className="kitchen-card__print" />
      </div>
    </li>
  );
}

// -------------------------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------------------------

type ConsoleT = ReturnType<typeof useTranslation>['t'];

/**
 * The order's human number the display and print show — "#0421" (F-FNB-04) — or, for a moment before it is
 * allocated (and on an older order), a short tag from the order's id.
 */
export function orderTag(ticket: Pick<KitchenTicket, 'orderNumber' | 'saleId' | 'orderId'>): string {
  const number = formatOrderNumber(ticket.orderNumber);
  if (number !== null) return number;
  const id = ticket.saleId ?? ticket.orderId ?? '';
  const tail = id.split('_').at(-1) ?? id;
  return `#${tail.slice(-5).toUpperCase()}`;
}

/** What Cancel order opens for a ticket: its sale (a counter or kiosk order) or its older food order. */
function cancelTargetOf(ticket: KitchenTicket): CancelTarget {
  const tag = orderTag(ticket);
  return ticket.saleId !== null ? { kind: 'sale', id: ticket.saleId, tag } : { kind: 'food_order', id: ticket.orderId ?? '', tag };
}

/** A station's label for the display: its name, or "PC {number}" (spec/13 §4). */
function labelForStation(station: Station, t: ConsoleT): string {
  return station.name ?? t('kitchen.pc', { number: station.number });
}

/** Whole minutes a ticket has waited since it was placed. */
function minutesSince(iso: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
}

/** The card's urgency by age (spec/13 §6.2: "colour by age"). */
function ageTone(minutes: number): 'fresh' | 'waiting' | 'late' {
  if (minutes >= 10) return 'late';
  if (minutes >= 5) return 'waiting';
  return 'fresh';
}

/** A ticking clock so the age colour and the minutes update without a refetch. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
